import React, { useMemo, useState } from 'react';
import { AlertTriangle, Info, TrendingUp, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import useFrictionMetrics from '../../hooks/useFrictionMetrics';

interface Props {
  processMapId: number;
  processMapName?: string;
}

type SortKey = 'score' | 'name' | 'error' | 'effort' | 'time' | 'handoff' | 'exception';

const LEVEL_CONFIG = {
  critical: { label: 'Critical', color: '#f56565', bg: '#fff5f5', text: '#742a2a', range: '76–100' },
  high:     { label: 'High',     color: '#ed8936', bg: '#fffaf0', text: '#7b341e', range: '51–75' },
  moderate: { label: 'Moderate', color: '#ecc94b', bg: '#fffff0', text: '#744210', range: '26–50' },
  low:      { label: 'Low',      color: '#48bb78', bg: '#f0fff4', text: '#1c4532', range: '0–25' },
};

const DIMENSIONS = [
  { key: 'error',     label: 'Error Rate',    scoreKey: 'error',     rawKey: 'error_rate',         unit: '%',      weight: 20, definition: 'Percentage of times this step produces errors or requires rework out of total executions.' },
  { key: 'effort',    label: 'Manual Effort',  scoreKey: 'effort',    rawKey: 'manual_hours_weekly', unit: 'hrs/wk', weight: 25, definition: 'Total hours per week spent on manual work at this step, including data entry, reviews, and approvals.' },
  { key: 'time',      label: 'Cycle Time',     scoreKey: 'time',      rawKey: 'cycle_time_hours',   unit: 'hrs',    weight: 25, definition: 'Average elapsed time from when this step starts to when it completes, including wait time.' },
  { key: 'handoff',   label: 'Handoffs',       scoreKey: 'handoff',   rawKey: 'handoff_count',      unit: '',       weight: 15, definition: 'Number of times work is passed between different people, teams, or systems during this step.' },
  { key: 'exception', label: 'Exceptions',     scoreKey: 'exception', rawKey: 'exception_rate',     unit: '%',      weight: 15, definition: 'Percentage of cases that deviate from the standard path and require special handling.' },
] as const;

function scoreToColor(score: number): string {
  if (score >= 76) return '#f56565';
  if (score >= 51) return '#ed8936';
  if (score >= 26) return '#ecc94b';
  return '#48bb78';
}

function scoreToLevel(score: number): string {
  if (score >= 76) return 'Critical';
  if (score >= 51) return 'High';
  if (score >= 26) return 'Moderate';
  return 'Low';
}

function scoreToBg(score: number): string {
  if (score >= 76) return '#fff5f5';
  if (score >= 51) return '#fffaf0';
  if (score >= 26) return '#fffff0';
  return '#f0fff4';
}

function truncate(s: string, max: number): string {
  return s && s.length > max ? s.slice(0, max - 1) + '…' : (s || '');
}

function getPrimaryDriver(ns: any): { label: string; score: number } | null {
  if (!ns) return null;
  let max = { label: '', score: 0 };
  DIMENSIONS.forEach(d => {
    const s = ns[d.scoreKey] ?? 0;
    if (s > max.score) max = { label: d.label, score: s };
  });
  return max.score > 0 ? max : null;
}

function formatRaw(raw: any, unit: string): string {
  if (raw == null) return '—';
  const v = Number(raw);
  if (unit === '%') return `${v.toFixed(1)}%`;
  if (unit === 'hrs/wk') return `${v.toFixed(1)} hrs/wk`;
  if (unit === 'hrs') return `${v.toFixed(1)} hrs`;
  return v % 1 === 0 ? String(Math.round(v)) : v.toFixed(1);
}

const FrictionHeatmapChart: React.FC<Props> = ({ processMapId, processMapName }) => {
  const { frictionData, isLoading, error, fetchFrictionData } = useFrictionMetrics();
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [sortDesc, setSortDesc] = useState(true);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [showMethodology, setShowMethodology] = useState(false);

  React.useEffect(() => {
    if (processMapId) {
      fetchFrictionData(processMapId);
    }
  }, [processMapId]);

  const steps = useMemo(() => {
    const raw = (frictionData?.steps || []).filter((s: any) => s.has_data);
    return [...raw].sort((a: any, b: any) => {
      let va = 0, vb = 0;
      if (sortKey === 'score') { va = a.friction_score || 0; vb = b.friction_score || 0; }
      else if (sortKey === 'name') { return sortDesc ? b.step_name?.localeCompare(a.step_name) : a.step_name?.localeCompare(b.step_name); }
      else {
        va = a.normalized_scores?.[sortKey] || 0;
        vb = b.normalized_scores?.[sortKey] || 0;
      }
      return sortDesc ? vb - va : va - vb;
    });
  }, [frictionData, sortKey, sortDesc]);

  const summary = frictionData?.summary;

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDesc(d => !d);
    else { setSortKey(key); setSortDesc(true); }
  };

  const SortIndicator = ({ k }: { k: SortKey }) =>
    sortKey === k ? <span className="ml-0.5 text-purple-500">{sortDesc ? '↓' : '↑'}</span> : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <div className="animate-spin w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full mr-3" />
        <span className="text-sm">Loading friction data…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span>Failed to load friction data: {error}</span>
      </div>
    );
  }

  if (!frictionData || steps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <Info className="w-10 h-10 mb-3 text-gray-300" />
        <p className="font-medium text-gray-500">No friction data available</p>
        <p className="text-sm text-gray-400 mt-1">Click "Friction Heatmap" in Analysis to auto-generate estimates.</p>
      </div>
    );
  }

  const actionableSteps = steps.filter((s: any) => (s.friction_score || 0) >= 51);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-gray-50">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-red-500" />
          <span className="font-bold text-gray-800">
            Process Friction Heatmap
            {processMapName && <span className="text-gray-400 font-normal ml-2">· {processMapName}</span>}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">{steps.length} steps analyzed</span>
          {summary && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: scoreToBg(summary.average_friction), color: scoreToColor(summary.average_friction) }}>
              Avg: {summary.average_friction.toFixed(1)} / 100
            </span>
          )}
          <button
            onClick={() => setShowMethodology(!showMethodology)}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-purple-600 transition-colors"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            How it works
          </button>
        </div>
      </div>

      {/* Methodology panel */}
      {showMethodology && (
        <div className="px-5 py-4 border-b border-gray-100 bg-blue-50/50">
          <div className="text-xs text-gray-700 space-y-3">
            <div>
              <div className="font-semibold text-gray-800 mb-1">How Friction Scores are Calculated</div>
              <p className="text-gray-600 mb-2">Each step is analyzed across 5 dimensions. Raw values are normalized to a 0–100 scale, then combined using weighted averaging to produce a Total Score.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {DIMENSIONS.map(d => (
                <div key={d.key} className="bg-white rounded-lg p-2.5 border border-gray-200">
                  <div className="font-semibold text-gray-800">{d.label} <span className="text-gray-400 font-normal">({d.weight}% weight)</span></div>
                  <div className="text-gray-500 mt-0.5">{d.definition}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 pt-1">
              <span className="font-semibold text-gray-800">Score Ranges:</span>
              {Object.entries(LEVEL_CONFIG).map(([key, cfg]) => (
                <span key={key} className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: cfg.color }} />
                  <span>{cfg.label} ({cfg.range})</span>
                </span>
              ))}
            </div>
            <div className="text-gray-500 italic">Data sources: "Grounded" = measured from system data. "Estimated" = AI-generated using document context and industry benchmarks.</div>
          </div>
        </div>
      )}

      {/* Summary KPI strip */}
      {summary && (
        <div className="grid grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
          {([
            { label: 'Critical', count: summary.critical_count, level: 'critical' as const },
            { label: 'High',     count: summary.high_count,     level: 'high' as const },
            { label: 'Moderate', count: summary.moderate_count, level: 'moderate' as const },
            { label: 'Low',      count: summary.low_count,      level: 'low' as const },
          ]).map(({ label, count, level }) => {
            const cfg = LEVEL_CONFIG[level];
            return (
              <div key={level} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: cfg.color }} />
                  <span className="text-xs text-gray-600">{label} ({cfg.range})</span>
                </div>
                <span className="text-lg font-bold" style={{ color: cfg.color }}>{count}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Priority Actions */}
      {actionableSteps.length > 0 && (
        <div className="px-5 py-3 border-b border-gray-100 bg-red-50/30">
          <div className="text-xs font-semibold text-red-700 mb-1">{actionableSteps.length} step{actionableSteps.length > 1 ? 's' : ''} requiring attention</div>
          <div className="flex flex-wrap gap-2">
            {actionableSteps.slice(0, 5).map((s: any) => {
              const driver = getPrimaryDriver(s.normalized_scores);
              return (
                <span key={s.step_identifier} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-medium" style={{ background: scoreToBg(s.friction_score), color: scoreToColor(s.friction_score), border: `1px solid ${scoreToColor(s.friction_score)}30` }}>
                  {truncate(s.step_name || s.step_identifier, 20)}: {s.friction_score?.toFixed(0)}
                  {driver && <span className="text-gray-500">({driver.label})</span>}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Heatmap Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-2.5 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors w-52 sticky left-0 bg-gray-50 z-10" onClick={() => handleSort('name')}>
                Step Name <SortIndicator k="name" />
              </th>
              {DIMENSIONS.map(d => (
                <th key={d.key} className="text-center px-3 py-2.5 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors min-w-[100px]" onClick={() => handleSort(d.key as SortKey)}>
                  <div>{d.label} <SortIndicator k={d.key as SortKey} /></div>
                  <div className="font-normal text-[9px] text-gray-400 mt-0.5">{d.weight}% weight</div>
                </th>
              ))}
              <th className="text-center px-3 py-2.5 font-bold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors min-w-[90px]" onClick={() => handleSort('score')}>
                Total <SortIndicator k="score" />
                <div className="font-normal text-[9px] text-gray-400 mt-0.5">0–100</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {steps.map((step: any, idx: number) => {
              const ns = step.normalized_scores || {};
              const totalScore = step.friction_score || 0;
              const isEven = idx % 2 === 0;
              const isExpanded = expandedRow === (step.step_identifier || String(idx));
              const driver = getPrimaryDriver(ns);

              return (
                <React.Fragment key={step.step_identifier || idx}>
                  <tr
                    className={`border-b border-gray-100 hover:brightness-95 transition-all cursor-pointer ${isEven ? 'bg-white' : 'bg-gray-50/50'}`}
                    onClick={() => setExpandedRow(isExpanded ? null : (step.step_identifier || String(idx)))}
                  >
                    {/* Step name */}
                    <td className={`px-4 py-2.5 sticky left-0 z-10 ${isEven ? 'bg-white' : 'bg-gray-50'} border-r border-gray-100`}>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-8 rounded-full flex-shrink-0" style={{ background: step.friction_color || scoreToColor(totalScore) }} />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-800 leading-tight">{truncate(step.step_name || step.step_identifier || `Step ${idx + 1}`, 30)}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {step.data_source && (
                              <span className={`text-[10px] ${step.data_source === 'grounded' ? 'text-emerald-600' : 'text-amber-600'}`}>
                                {step.data_source === 'grounded' ? '✓ Measured' : '~ AI Estimated'}
                              </span>
                            )}
                            {driver && (
                              <span className="text-[10px] text-gray-400">Top issue: {driver.label}</span>
                            )}
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp className="w-3 h-3 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />}
                      </div>
                    </td>

                    {/* Dimension cells */}
                    {DIMENSIONS.map(d => {
                      const score = ns[d.scoreKey] ?? null;
                      const raw = step[d.rawKey];
                      return (
                        <td key={d.key} className="px-2 py-2 text-center">
                          {score != null ? (
                            <div className="inline-flex flex-col items-center">
                              <div className="rounded-md px-2.5 py-1.5 font-bold text-xs leading-none" style={{ background: scoreToBg(score), color: scoreToColor(score), border: `1px solid ${scoreToColor(score)}40`, minWidth: '48px' }}>
                                {formatRaw(raw, d.unit)}
                              </div>
                              <span className="text-[9px] text-gray-400 mt-0.5">{score.toFixed(0)}/100</span>
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      );
                    })}

                    {/* Total score */}
                    <td className="px-2 py-2 text-center">
                      <div className="inline-flex flex-col items-center">
                        <div className="rounded-md px-3 py-2 font-bold text-sm leading-none" style={{ background: scoreToColor(totalScore), color: 'white', minWidth: '48px' }}>
                          {totalScore.toFixed(0)}
                        </div>
                        <span className="text-[9px] text-gray-400 mt-0.5">{scoreToLevel(totalScore)}</span>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded detail row */}
                  {isExpanded && (
                    <tr className="bg-gray-50/80">
                      <td colSpan={7} className="px-6 py-4">
                        <div className="grid grid-cols-5 gap-3">
                          {DIMENSIONS.map(d => {
                            const score = ns[d.scoreKey] ?? 0;
                            const raw = step[d.rawKey];
                            return (
                              <div key={d.key} className="bg-white rounded-lg p-3 border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-semibold text-gray-700 text-xs">{d.label}</span>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: scoreToBg(score), color: scoreToColor(score) }}>{score.toFixed(0)}/100</span>
                                </div>
                                <div className="text-lg font-bold mb-1" style={{ color: scoreToColor(score) }}>
                                  {formatRaw(raw, d.unit)}
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                                  <div className="h-1.5 rounded-full transition-all" style={{ width: `${Math.min(score, 100)}%`, background: scoreToColor(score) }} />
                                </div>
                                <p className="text-[10px] text-gray-500 leading-tight">{d.definition}</p>
                                <div className="text-[10px] text-gray-400 mt-1">Weight: {d.weight}% of total score</div>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-5 py-2.5 border-t border-gray-100 bg-gray-50/50 text-[10px] text-gray-400">
        <span>Click any row for detailed breakdown · Click column headers to sort</span>
        <button onClick={() => setShowMethodology(!showMethodology)} className="text-purple-500 hover:text-purple-700 font-medium">
          {showMethodology ? 'Hide methodology' : 'How are scores calculated?'}
        </button>
      </div>
    </div>
  );
};

export default FrictionHeatmapChart;
